<template>
  <transition name="rbd-slide">
    <aside v-if="room" class="rbd" :key="room.roomId">
      <!-- header: room number is the headline, states are words not pills -->
      <div class="rbd__head">
        <div class="rbd__head-main">
          <div class="rbd__head-top">
            <span class="rbd__number tabular">{{ room.roomNumber }}</span>
            <span class="rbd__state" :class="`rbd__state--${stateTone}`">{{ $t(`roomBoard.occ.${room.occupancy}`) }}</span>
            <span class="rbd__cleanword" :class="`rbd__cleanword--${cleanTone}`">
              <i/>{{ $t(`roomBoard.clean.${room.cleaning}`) }}
            </span>
          </div>
          <div class="rbd__head-sub">
            <span v-if="room.categoryName">
              <span v-if="room.categoryColor" class="rbd__dot" :style="{background: room.categoryColor}"/>{{ room.categoryName }}
            </span>
            <span v-if="room.floorNumber != null">{{ $t('roomBoard.floor') }} {{ room.floorNumber }}</span>
            <span v-if="room.roomName">{{ room.roomName }}</span>
          </div>
        </div>
        <button type="button" class="rbd__close" @click="$emit('close')"><i class="fa-solid fa-xmark"/></button>
      </div>

      <!-- context-aware actions: the one thing to do for THIS room, first -->
      <div v-if="primaryAction" class="rbd__cta">
        <button type="button" class="rbd__btn rbd__btn--primary" :disabled="cleaningBusy" @click="primaryAction.run">
          <i :class="primaryAction.icon" class="mr-25"/>{{ primaryAction.label }}
        </button>
        <button v-if="secondaryAction" type="button" class="rbd__btn" :disabled="cleaningBusy" @click="secondaryAction.run">
          <i :class="secondaryAction.icon" class="mr-25"/>{{ secondaryAction.label }}
        </button>
      </div>

      <div class="rbd__scroll">
        <div v-if="imageSrc" class="rbd__photo"><img :src="imageSrc" alt=""/></div>

        <!-- attention first -->
        <div v-if="room.attention && room.attention.length" class="rbd__section">
          <div class="rbd__label rbd__label--danger">{{ $t('analyticsHub.attentionRequired') }}</div>
          <div v-for="code in room.attention" :key="code" class="rbd__att">
            <i/>{{ $t(`roomBoard.attention.${code}`) }}
          </div>
        </div>

        <!-- departing guest (turnover) -->
        <div v-if="room.departing" class="rbd__section">
          <div class="rbd__label">{{ $t('roomBoard.departingGuest') }}</div>
          <div class="rbd__guest">
            <div class="rbd__guest-main">
              <b>{{ room.departing.guestName || '—' }}</b>
              <small v-if="room.departing.checkOutTime" class="tabular">{{ $t('roomBoard.until') }} {{ room.departing.checkOutTime }}</small>
            </div>
            <button type="button" class="rbd__link" @click="$emit('view-reservation', room.departing.contractId)">
              {{ $t('roomBoard.viewReservation') }} <i class="fas fa-arrow-right-long"/>
            </button>
          </div>
        </div>

        <!-- current / arriving guest -->
        <div v-if="room.reservation" class="rbd__section">
          <div class="rbd__label">
            {{ room.occupancy === 'ARRIVAL' || room.occupancy === 'TURNOVER' ? $t('roomBoard.arrivingGuest') : $t('roomBoard.currentGuest') }}
          </div>
          <div class="rbd__guest">
            <div class="rbd__guest-main">
              <b>{{ room.reservation.guestName || '—' }}</b>
              <small>
                <span v-if="room.reservation.guests">{{ room.reservation.guests }} {{ $t('roomBoard.guestsWord') }}</span>
                <span v-if="room.reservation.guests && room.reservation.phone"> · </span>
                <span v-if="room.reservation.phone" class="tabular">{{ room.reservation.phone }}</span>
              </small>
            </div>
            <button v-if="room.reservation.clientId" type="button" class="rbd__link" @click="viewClient(room.reservation.clientId)">
              {{ $t('roomBoard.viewClient') }} <i class="fas fa-arrow-right-long"/>
            </button>
          </div>

          <!-- stay: flat three-column line -->
          <div class="rbd__stay">
            <div class="rbd__stay-cell">
              <span>{{ $t('roomBoard.checkIn') }}</span>
              <b class="tabular">{{ room.reservation.checkIn | moment('DD.MM.YYYY') }}</b>
              <small class="tabular">{{ room.reservation.checkInTime || '&nbsp;' }}</small>
            </div>
            <div class="rbd__stay-cell">
              <span>{{ $t('roomBoard.checkOut') }}</span>
              <b class="tabular">{{ room.reservation.checkOut | moment('DD.MM.YYYY') }}</b>
              <small class="tabular">{{ room.reservation.checkOutTime || '&nbsp;' }}</small>
            </div>
            <div class="rbd__stay-cell rbd__stay-cell--end">
              <span>{{ $t('roomBoard.nights') }}</span>
              <b class="tabular">{{ room.reservation.nights || '—' }}</b>
            </div>
          </div>

          <div class="rbd__rows">
            <div v-if="room.reservation.platform" class="rbd__row">
              <span>{{ $t('analyticsHub.platform') }}</span>
              <b><platform-logo :platform="room.reservation.platform" size="13px"/> {{ room.reservation.platform }}</b>
            </div>
            <div v-if="room.reservation.platformReservationId" class="rbd__row">
              <span>{{ $t('roomBoard.reservationId') }}</span><b class="tabular">{{ room.reservation.platformReservationId }}</b>
            </div>
            <div v-if="room.reservation.dayRate" class="rbd__row">
              <span>{{ $t('roomBoard.dayRate') }}</span><b class="tabular">{{ room.reservation.dayRate | numberFormatter }} €</b>
            </div>
            <div v-if="room.reservation.totalPayment" class="rbd__row">
              <span>{{ $t('roomBoard.stayTotal') }}</span><b class="tabular">{{ room.reservation.totalPayment | numberFormatter }} €</b>
            </div>
          </div>
          <p v-if="room.reservation.guestNote" class="rbd__note">{{ room.reservation.guestNote }}</p>

          <div class="rbd__quiet-actions">
            <button type="button" class="rbd__link" @click="$emit('view-reservation', room.reservation.contractId)">
              <i class="far fa-eye mr-25"/>{{ $t('roomBoard.viewReservation') }}
            </button>
            <button type="button" class="rbd__link" @click="openContract(room.reservation.contractId)">
              <i class="fa-solid fa-file-contract mr-25"/>{{ $t('roomBoard.contract') }}
            </button>
          </div>
        </div>

        <!-- next arrival for empty rooms -->
        <div v-else-if="room.nextArrival" class="rbd__section">
          <div class="rbd__label">{{ $t('roomBoard.nextArrival') }}</div>
          <div class="rbd__guest">
            <div class="rbd__guest-main">
              <b>{{ room.nextArrival.guestName || '—' }}</b>
              <small class="tabular">{{ room.nextArrival.date | moment('DD.MM.YYYY') }}</small>
            </div>
            <button type="button" class="rbd__link" @click="$emit('view-reservation', room.nextArrival.contractId)">
              {{ $t('roomBoard.viewReservation') }} <i class="fas fa-arrow-right-long"/>
            </button>
          </div>
        </div>

        <!-- housekeeping -->
        <div class="rbd__section">
          <div class="rbd__label">{{ $t('roomBoard.housekeeping') }}</div>
          <div class="rbd__rows">
            <div class="rbd__row">
              <span>{{ $t('status') }}</span>
              <b class="rbd__cleanword" :class="`rbd__cleanword--${cleanTone}`"><i/>{{ $t(`roomBoard.clean.${room.cleaning}`) }}</b>
            </div>
            <div v-if="cleaningInfo && cleaningInfo.cleanedTime" class="rbd__row">
              <span>{{ $t('roomBoard.lastCleaned') }}</span>
              <b class="tabular">{{ cleaningInfo.cleanedTime | moment('DD.MM HH:mm') }}</b>
            </div>
            <div v-if="cleaningInfo && cleaningInfo.cleaner" class="rbd__row">
              <span>{{ $t('roomBoard.cleanedBy') }}</span><b>{{ cleaningInfo.cleaner }}</b>
            </div>
            <div v-if="cleaningInfo && cleaningInfo.requestedCleanTime && room.cleaning !== 'CLEAN'" class="rbd__row">
              <span>{{ $t('roomBoard.dueSince') }}</span>
              <b class="tabular">{{ cleaningInfo.requestedCleanTime | moment('DD.MM HH:mm') }}</b>
            </div>
          </div>
          <p v-if="cleaningInfo && cleaningInfo.notes" class="rbd__note">{{ cleaningInfo.notes }}</p>
        </div>

        <!-- maintenance -->
        <div v-if="room.damages && room.damages.length" class="rbd__section">
          <div class="rbd__label rbd__label--danger">{{ $t('roomBoard.maintenance') }}</div>
          <div v-for="damage in room.damages" :key="damage.id" class="rbd__damage">
            <b>{{ damage.description || $t('roomBoard.maintenance') }}</b>
            <small>
              <span v-if="damage.reportedBy">{{ damage.reportedBy }} · </span>
              <span class="tabular">{{ damage.reportedTime | moment('DD.MM.YYYY HH:mm') }}</span>
            </small>
          </div>
        </div>

        <!-- room facts last: reference data, not operations -->
        <div class="rbd__section">
          <div class="rbd__label">{{ $t('roomBoard.room') }}</div>
          <div class="rbd__rows">
            <div v-if="room.keyCode" class="rbd__row"><span>{{ $t('roomBoard.keyCode') }}</span><b class="tabular">{{ room.keyCode }}</b></div>
            <div v-if="room.fullAddress" class="rbd__row"><span>{{ $t('roomBoard.address') }}</span><b>{{ room.fullAddress }}</b></div>
          </div>
        </div>
      </div>
    </aside>
  </transition>
</template>

<script>
import {mapActions} from 'vuex'
import PlatformLogo from '@/components/widgets/PlatformLogo.vue'

export default {
  name: 'RoomBoardDrawer',
  components: {PlatformLogo},
  props: {
    room: {type: Object, default: null},
  },
  data: () => ({cleaningBusy: false}),
  computed: {
    stateTone() {
      return {OCCUPIED: 'blue', ARRIVAL: 'blue', DEPARTURE: 'amber', TURNOVER: 'violet', AVAILABLE: 'mute', MAINTENANCE: 'gray'}[this.room.occupancy] || 'mute'
    },
    cleanTone() {
      return {CLEAN: 'ok', DIRTY: 'bad', IN_PROGRESS: 'work', OVERDUE: 'bad'}[this.room.cleaning] || 'ok'
    },
    cleaningInfo() { return this.room.cleaningInfo || null },
    /** housekeeping actions only exist while there IS an open task */
    openTaskId() {
      return this.cleaningInfo && this.room.cleaning !== 'CLEAN' ? this.cleaningInfo.taskId : null
    },
    /** the room's next operational step decides the primary button */
    primaryAction() {
      if (this.openTaskId && (this.room.cleaning === 'DIRTY' || this.room.cleaning === 'OVERDUE')) {
        return {label: this.$t('roomBoard.startCleaning'), icon: 'fas fa-rotate', run: () => this.setCleaningStatus('CLEANING_IN_PROGRESS')}
      }
      if (this.openTaskId && this.room.cleaning === 'IN_PROGRESS') {
        return {label: this.$t('roomBoard.markCleaned'), icon: 'fas fa-check', run: () => this.setCleaningStatus('CLEANED')}
      }
      if (this.room.reservation) {
        return {label: this.$t('roomBoard.viewReservation'), icon: 'far fa-eye', run: () => this.$emit('view-reservation', this.room.reservation.contractId)}
      }
      return null
    },
    secondaryAction() {
      if (this.openTaskId && (this.room.cleaning === 'DIRTY' || this.room.cleaning === 'OVERDUE')) {
        return {label: this.$t('roomBoard.markCleaned'), icon: 'fas fa-check', run: () => this.setCleaningStatus('CLEANED')}
      }
      return null
    },
    imageSrc() {
      if (!this.room.imageUrl) return null
      return (this.room.imageUrl.startsWith('http') ? '' : this.getRealEstatesWebSite || '') + this.room.imageUrl
    },
    getRealEstatesWebSite() {
      return this.$store.getters['data/getRealEstatesWebSite']
    },
  },
  methods: {
    ...mapActions('cleaning', ['saveCleaningTask']),
    viewClient(clientId) {
      this.$root.$emit('crm::client::select', clientId)
    },
    openContract(contractId) {
      window.open(`/real_estate/contracts/rent/${contractId}`, '_blank')
    },
    setCleaningStatus(status) {
      if (!this.openTaskId) return
      this.cleaningBusy = true
      this.saveCleaningTask({id: this.openTaskId, request: {status}})
          .finally(() => {
            this.cleaningBusy = false
            this.$emit('refresh')
          })
    },
  },
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }

/*
 * Same flat language as the board: white surface, hairlines between
 * sections, uppercase micro-labels, values as typography. The only filled
 * element is the single context-aware primary button.
 */
.rbd {
  position: sticky; top: 84px;
  width: 100%;
  max-height: calc(100vh - 100px);
  display: flex; flex-direction: column;
  background: #fff;
  border: 1px solid rgba(23, 30, 48, .12);
  border-radius: 10px;
  overflow: hidden;
}

.rbd__head {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 8px;
  padding: 14px 16px 12px;
}
.rbd__head-top { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.rbd__number { font-size: 1.45rem; font-weight: 800; color: #171e30; letter-spacing: -.02em; line-height: 1; }
.rbd__state {
  font-size: .62rem; font-weight: 800; letter-spacing: .1em; text-transform: uppercase;
  &--blue { color: #3b6ff6; }
  &--amber { color: #c07f14; }
  &--violet { color: #8a5cf6; }
  &--gray { color: #8a93a5; }
  &--mute { color: #9aa2b1; }
}
.rbd__cleanword {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: .68rem; font-weight: 700;
  i { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
  &--ok { color: #2fa46a; i { background: #2fa46a; } }
  &--bad { color: #cc3f3f; i { background: #e05252; } }
  &--work { color: #c07f14; i { background: #e8a23d; } }
}
.rbd__head-sub {
  display: flex; align-items: center; flex-wrap: wrap; gap: 4px 12px; margin-top: 5px;
  font-size: .7rem; font-weight: 600; color: #9aa2b1;
  span { display: inline-flex; align-items: center; gap: 5px; }
}
.rbd__dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.rbd__close {
  width: 28px; height: 28px; border: none; border-radius: 7px; cursor: pointer; flex-shrink: 0;
  background: transparent; color: #9aa2b1; font-size: .8rem;
  display: inline-flex; align-items: center; justify-content: center;
  &:hover { background: rgba(23, 30, 48, .06); color: #171e30; }
  &:focus { outline: none; }
}

.rbd__cta { display: flex; gap: 7px; padding: 0 16px 13px; }
.rbd__btn {
  flex: 1; display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid rgba(23, 30, 48, .14); border-radius: 7px; background: transparent;
  color: #4a5468; font-size: .74rem; font-weight: 700; cursor: pointer;
  padding: 7px 12px; white-space: nowrap; transition: all .12s;
  &:hover { border-color: #171e30; color: #171e30; }
  &:focus { outline: none; }
  &:disabled { opacity: .5; cursor: default; }
  &--primary {
    background: #171e30; border-color: #171e30; color: #fff;
    &:hover { background: #232d49; border-color: #232d49; color: #fff; }
  }
}

.rbd__scroll { overflow-y: auto; padding: 0 16px 16px; border-top: 1px solid rgba(23, 30, 48, .07); }
.rbd__photo {
  border-radius: 8px; overflow: hidden; margin-bottom: 4px;
  img { width: 100%; height: 128px; object-fit: cover; display: block; }
}

.rbd__section { padding: 12px 0; border-top: 1px solid rgba(23, 30, 48, .07); &:first-child { border-top: none; } }
.rbd__label {
  font-size: .6rem; font-weight: 800; text-transform: uppercase; letter-spacing: .12em;
  color: #9aa2b1; margin-bottom: 8px;
  &--danger { color: #cc3f3f; }
}

.rbd__guest { display: flex; align-items: center; gap: 10px; }
.rbd__guest-main {
  flex: 1; min-width: 0; display: flex; flex-direction: column;
  b { font-size: .88rem; font-weight: 700; color: #171e30; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  small { font-size: .68rem; color: #9aa2b1; margin-top: 1px; }
}
.rbd__link {
  border: none; background: none; padding: 0; cursor: pointer; flex-shrink: 0;
  font-size: .68rem; font-weight: 700; color: #3b6ff6;
  i { font-size: .56rem; }
  &:hover { text-decoration: underline; }
  &:focus { outline: none; }
}

.rbd__stay { display: flex; gap: 18px; margin: 11px 0 4px; }
.rbd__stay-cell {
  display: flex; flex-direction: column; min-width: 0;
  span { font-size: .58rem; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: #9aa2b1; }
  b { font-size: .8rem; font-weight: 700; color: #171e30; margin-top: 2px; }
  small { font-size: .64rem; color: #9aa2b1; }
  &--end { margin-left: auto; text-align: right; }
}

.rbd__rows { display: flex; flex-direction: column; }
.rbd__row {
  display: flex; align-items: baseline; justify-content: space-between; gap: 12px;
  padding: 4px 0; min-width: 0;
  span { font-size: .64rem; font-weight: 600; color: #9aa2b1; white-space: nowrap; }
  b { font-size: .76rem; font-weight: 600; color: #2a3348; text-align: right; min-width: 0; overflow: hidden; text-overflow: ellipsis; display: inline-flex; align-items: center; gap: 5px; }
}
.rbd__note { font-size: .74rem; color: #4a5468; margin: 6px 0 0; white-space: pre-line; }

.rbd__quiet-actions { display: flex; gap: 16px; margin-top: 9px; }

.rbd__att {
  display: flex; align-items: center; gap: 7px;
  font-size: .74rem; font-weight: 600; color: #b23535; padding: 3px 0;
  i { width: 6px; height: 6px; border-radius: 50%; background: #e05252; flex-shrink: 0; }
}

.rbd__damage {
  border-left: 2px solid #e05252; padding: 2px 0 2px 10px; margin-bottom: 7px;
  display: flex; flex-direction: column;
  &:last-child { margin-bottom: 0; }
  b { font-size: .76rem; font-weight: 600; color: #2a3348; }
  small { font-size: .64rem; color: #9aa2b1; }
}

.rbd-slide-enter-active, .rbd-slide-leave-active { transition: opacity .18s ease, transform .18s ease; }
.rbd-slide-enter, .rbd-slide-leave-to { opacity: 0; transform: translateX(14px); }

/* inside the mobile full-screen overlay the panel must not stick mid-screen */
@media (max-width: 1100px) {
  .rbd { position: static; max-height: none; }
}
</style>

<style lang="scss">
.dark-layout {
  .rbd { background: #232b42; border-color: rgba(255, 255, 255, .1); }
  .rbd__number { color: #eef1f7; }
  .rbd__state--blue { color: #7ba3ff; }
  .rbd__state--violet { color: #b393ff; }
  .rbd__state--amber { color: #e0a94e; }
  .rbd__close:hover { background: rgba(255, 255, 255, .08); color: #eef1f7; }
  .rbd__btn { border-color: rgba(255, 255, 255, .16); color: #aeb6c6; &:hover { border-color: #eef1f7; color: #eef1f7; } }
  .rbd__btn--primary {
    background: #eef1f7; border-color: #eef1f7; color: #171e30;
    &:hover { background: #fff; border-color: #fff; color: #171e30; }
  }
  .rbd__scroll { border-color: rgba(255, 255, 255, .08); }
  .rbd__section { border-color: rgba(255, 255, 255, .08); }
  .rbd__guest-main b, .rbd__stay-cell b { color: #eef1f7; }
  .rbd__row b, .rbd__damage b { color: #cfd5e1; }
  .rbd__note { color: #aeb6c6; }
  .rbd__link { color: #7ba3ff; }
}
</style>
